import { ActionContext, Module } from 'vuex';
import RootStateType from '@/store/types';
import store, { updateModuleState } from '@/store';
import generateMutations, { UPDATE_STATE_TYPE } from '@/store/generate-mutations';
import { RouterV2Enum } from '@/router/router-names';
import { TempAmountSceneEnum, EventLoopNotifySceneEnum } from '@/api/tempAmount/types';
import RouterHelper from '@/shared/utils/routerHelper';
import { dispatchActions } from '@/store/dispatchActions';
import { getSkPreRequestData, skNativeContainer } from '@/api/native/container';
import HomeV2Status, { getHaiXiaoQueryStatus } from '@/api/homeV2Status';
import { PageSceneEnum } from '@/api/common/types';
import { UserCreditType } from '@/api/homeV2Status/types';
import { getGalileoAb } from '@/shared/galileoAb';
import { ubtModuleExposeNew, ubtSendError } from '@/shared/utils/ubt';
import { ABResEnum } from '@/enums/abExperiment';
import type { HomeStoreStateType } from './types';

/**
 * 首页store数据源，以下场景建议使用：
 * 1. 多组件共享的数据
 * 2. 首页全局的数据状态
 */
const moduleStore: Module<HomeStoreStateType, RootStateType> = {
  namespaced: true,
  state: {
    /** 额度更新-弹窗的展示状态 */
    creditAmountAlterPopupVisible: false,
    /** 额度更新-弹窗的额度 */
    creditAmountAlterPopupAmount: undefined,
    /** 用户授信数据 */
    userCreditData: null,
    /** 是否只展示利率下限 */
    showRateTextExp: null,
    /** 是否展示品牌化文案（首页头部标题栏/金融卡片） */
    showBrandingExp: null,
    /** 海消经营准入结果 */
    haiXiaoQueryData: null,
  },
  actions: {
    /**
     * 获取用户授信信息(首页关键数据)
     */
    async getUserCreditData(
      context: ActionContext<HomeStoreStateType, RootStateType>,
    ): Promise<void> {
      try {
        /** 读取客户端预请求-授信数据 */
        const nativeUserCredit = await getSkPreRequestData({
          project: 'AppFinanceH5Web',
          action: 'f.loan.panel.queryUserCredit',
        });

        // 是否从客户端容器化中获取到预请求数据
        const hasNativeData = Object.keys(nativeUserCredit).length > 0;

        // 获取授信数据：通过客户端容器化方法获取数据，若无数据则请求接口兜底
        const getUserCredit = () => {
          const res = hasNativeData
            ? Promise.resolve(nativeUserCredit as UserCreditType)
            : dispatchActions.userCreditStatus.getUserCredit();
          res.then(() => {
            // 埋点 获取授信数据获取成功
            ubtModuleExposeNew('finance_loan_home', 'finance_loan_home_creditData_success', {
              afterNavigatorStartTime: Date.now() - window.performance.timing.navigationStart,
              link: window.location.href,
            });
          });
          return res;
        };

        /** 首页3个关键接口数据：授信数据、订单信息、资料填写状态 */
        const combineCreditData = await Promise.all([
          getUserCredit(),
          HomeV2Status.getUserLoan(),
          dispatchActions.userProfileProgress.getUserInfoProgress(),
        ]);

        // 埋点 首页所有业务数据获取成功
        ubtModuleExposeNew('finance_loan_home', 'finance_loan_home_allBusinessData_success', {
          afterNavigatorStartTime: Date.now() - window.performance.timing.navigationStart,
          link: window.location.href,
        });

        // 判断在容器化中，预请求获取 成功/失败 的埋点
        skNativeContainer().then((isNativeContainer) => {
          if (isNativeContainer) {
            const nativeDataResEventId = hasNativeData ? 'finance_container_tabbar_pre_success' : 'finance_container_tabbar_pre_fail';
            ubtModuleExposeNew('finance_container_tabbar', nativeDataResEventId);
          }
        });

        /** 拼接用户授信数据 */
        const userCreditData = {
          ...(combineCreditData[0]).businessData,
          ...combineCreditData[1],
          ...combineCreditData[2],
        };

        context.dispatch(UPDATE_STATE_TYPE, {
          userCreditData,
        });

        // 同步更新老版首页的store数据
        await store.dispatch('home/updateCreditData', {
          creditData: userCreditData,
        });

        return Promise.resolve();
      } catch (e) {
        return Promise.resolve();
      }
    },

    /** 多场景通用轮询：临额、预授信下发 */
    async mutliSceneLoopQueryIssued(): Promise<void> {
      const loopSceneList: EventLoopNotifySceneEnum[] = [];

      if (store.state.home.creditData?.creditStatus) {
        const notExistTempAmount = await dispatchActions.tempAmount.checkNotExistTempAmount();

        /** 用户若已有授信额度 && 无临额，则尝试申请 "授信额度不足步长"的场景 */
        if (notExistTempAmount) {
          await dispatchActions.tempAmount.requestApplyTempAmount({
            scene: TempAmountSceneEnum.AMOUNT_NOT_ENOUGH,
          });
        } else if (store.state.tempAmount.tempAmountStatus?.tempAmountApplying) {
          loopSceneList.push(EventLoopNotifySceneEnum.TEMP_AMOUNT);
        }
      }
      /** 若是从流量分发页返回的，则轮询预授信的下发 */
      if (RouterHelper.isLastPageRoute(RouterV2Enum.preApplyChoose)) {
        loopSceneList.push(EventLoopNotifySceneEnum.PRE_CREDIT);
      }

      if (loopSceneList.length) {
        await dispatchActions.tempAmount.commonPollingNotifyIssued({
          loopSceneList,
        });
      }
    },
    async getShowRateFloorExpResult(
      context: ActionContext<HomeStoreStateType, RootStateType>,
    ): Promise<void> {
      if (typeof context.state.showRateTextExp === 'boolean') {
        return;
      }

      try {
        /** 伽利略ab实验：https://ab.hellobike.cn/experiment/detail/202511171125222919?labGuid=202211211841286660&isDetail=true */
        const res = await getGalileoAb().getAbResCatchError('202511171125222919', '202511171125223004');

        context.dispatch(UPDATE_STATE_TYPE, { showRateTextExp: res?.result === ABResEnum.HINT });
      } catch (error) {
        ubtSendError({
          name: 'getShowRateFloorExpResult-error',
          message: (error as Error).message,
        });
      }
    },
    async getShowBrandingExpResult(
      context: ActionContext<HomeStoreStateType, RootStateType>,
    ): Promise<void> {
      if (typeof context.state.showBrandingExp === 'boolean') {
        return;
      }

      try {
        /** 伽利略ab实验：https://ab.hellobike.cn/experiment/detail/202512181533042675?isDetail=true
         * 实验作用：首页加强品牌化-首页头部标题栏/金融卡片增加品牌化文案展示
         */
        const res = await getGalileoAb().getAbResCatchError('202512181533042675', '202512181533042851');

        context.dispatch(UPDATE_STATE_TYPE, { showBrandingExp: res?.result === ABResEnum.HINT });
      } catch (error) {
        ubtSendError({
          name: 'getShowBrandingExpResult-error',
          message: (error as Error).message,
        });
      }
    },

    /**
     * 获取海消广告位展示状态，以及是否隐藏授信申请入口
     */
    async getHaiXiaoQueryStatusResult(
      context: ActionContext<HomeStoreStateType, RootStateType>,
      payload: { scene: PageSceneEnum },
    ): Promise<void> {
      const { scene } = payload;
      try {
        const res = await getHaiXiaoQueryStatus(scene);
        context.dispatch(UPDATE_STATE_TYPE, { haiXiaoQueryData: res });
      } catch (error) {
        ubtSendError({
          name: 'getHaiXiaoQueryStatus-error',
          message: (error as Error).message,
        });
      }
    },
  },
};

/** 首页store对外清晰化ts类型的actions调用 */
export const homeStoreActions = {
  getUserCreditData: (): Promise<void> => store.dispatch('homeStore/getUserCreditData') as Promise<void>,
  getHaiXiaoQueryStatusResult: (scene: PageSceneEnum): Promise<void> => store.dispatch('homeStore/getHaiXiaoQueryStatusResult', { scene }) as Promise<void>,
  mutliSceneLoopQueryIssued: (): Promise<void> => store.dispatch('homeStore/mutliSceneLoopQueryIssued') as Promise<void>,
  setCreditAmountAlterPopupVisible: (visible: boolean, creditAmount?: string): Promise<void> => updateModuleState('homeStore', {
    creditAmountAlterPopupVisible: visible,
    creditAmountAlterPopupAmount: creditAmount,
  }) as Promise<void>,
};

store.registerModule(
  'homeStore',
  generateMutations<HomeStoreStateType>(moduleStore),
  {
    preserveState: false,
  },
);
